import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, Checkbox, Chip, CircularProgress, Modal, Snackbar, Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { FiSearch } from 'react-icons/fi';
import ProductEdit from './ProductEdit';
import ProductCreate from './ProductCreate';
import DeleteIcon from '@mui/icons-material/Delete';
import { motion } from 'framer-motion';
import { BASE_URL } from '../../utils/endpoints';

const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
};

const ProductList = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10); // Default to 10 rows per page
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openEdit, setOpenEdit] = useState(false);
    const [openCreate, setOpenCreate] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [confirmDelete, setConfirmDelete] = useState(false);

    const handleOpen = (product) => {
        setSelectedProduct(product);
        setOpenEdit(true);
    };

    const handleClose = () => {
        setOpenEdit(false);
        setSelectedProduct(null);
    };

    const handleOpenCreate = () => {
        setOpenCreate(true);
    };

    const handleCloseCreate = () => {
        setOpenCreate(false);
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const handleConfirmDeleteOpen = () => {
        if (selectedRows.length > 0) {
            setConfirmDelete(true);
        } else {
            setSnackbarMessage('Please select at least one product to delete');
            setSnackbarSeverity('warning');
            setOpenSnackbar(true);
        }
    };

    const handleConfirmDeleteClose = () => {
        setConfirmDelete(false);
    };

    const handleBulkDelete = async () => {
        try {
            const deletePromises = selectedRows.map(id =>
                fetch(`${BASE_URL}/products/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }).then(response => {
                    if (!response.ok) {
                        throw new Error(`Failed to delete product with ID ${id}`);
                    }
                    return response;
                })
            );
            const responses = await Promise.all(deletePromises);
            const allSuccessful = responses.every(response => response.ok);

            if (allSuccessful) {
                setProducts(products.filter(product => !selectedRows.includes(product.id)));
                setSelectedRows([]);
                setSnackbarMessage(`Successfully deleted ${selectedRows.length} product(s)`);
                setSnackbarSeverity('success');
            } else {
                setSnackbarMessage('Failed to delete some products');
                setSnackbarSeverity('error');
            }
        } catch (error) {
            console.error('Error deleting products:', error);
            setSnackbarMessage('Error deleting products: ' + error.message);
            setSnackbarSeverity('error');
        } finally {
            setOpenSnackbar(true);
            setConfirmDelete(false);
        }
    };

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BASE_URL}/products`);
            if (!response.ok) {
                throw new Error('Failed to fetch products');
            }
            const data = await response.json();

            const formattedProducts = (data.products || []).map(product => ({
                id: product._id || `fallback-${Math.random()}`,
                name: product.product_title || 'Unnamed Product',
                category: Array.isArray(product.categories) && product.categories.length > 0
                    ? product.categories[0]
                    : 'Unknown Category',
                price: product.product_price || '0',
                orgprice: product.original_price || '0',
                status: product.status === 'approved' ? 'Approved' : 'Pending',
                image: product.main_image || null,
                product_description: product.product_description || 'No description available',
                product_slider_images: product.product_slider_images || [],
                interests: product.interests || [],
                search_keywords: product.search_keywords || [],
            })).reverse();

            setProducts(formattedProducts);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const handleProductUpdated = () => {
        fetchProducts();
    };

    const handleProductCreated = () => {
        fetchProducts();
        handleCloseCreate();
    };

    const columns = [
        {
            field: 'select',
            headerName: '',
            width: 70,
            renderCell: (params) => (
                <Checkbox
                    checked={selectedRows.includes(params.row.id)}
                    onChange={(event) => {
                        setSelectedRows(
                            event.target.checked
                                ? [...selectedRows, params.row.id]
                                : selectedRows.filter(id => id !== params.row.id)
                        );
                    }}
                    sx={{
                        color: '#2e7d32',
                        '&.Mui-checked': {
                            color: '#2e7d32',
                        },
                    }}
                />
            ),
        },
        {
            field: 'name',
            headerName: <strong style={{ color: '#24803F' }}>Product</strong>,
            flex: 1,
            renderCell: (params) => (
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: 1,
                        p: 1,
                        bgcolor: '#f5f5f5',
                    }}>
                        <motion.img
                            src={params.row.image || `https://via.placeholder.com/40?text=${params.row.name?.[0] || 'N'}`}
                            style={{ width: 40, height: 40, borderRadius: 1, marginRight: 16 }}
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.4 }}
                        />
                        <Box>
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Typography
                                    variant='body2'
                                    sx={{
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        color: '#2e7d32',
                                        fontSize: '16px',
                                        textDecoration: 'none',
                                        position: 'absolute',
                                    }}
                                    onClick={() => handleOpen(params.row)}
                                >
                                    {truncateText(params.row.name || 'Unnamed Product')}
                                </Typography>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: 0.1 }}
                            >
                                <Typography variant='caption' color='text.secondary'>
                                    <strong>Category:</strong> {params.row.category || 'Unknown Category'}
                                </Typography>
                            </motion.div>
                        </Box>
                    </Box>
                </motion.div>
            ),
            width: 150,
        },
        {
            field: 'orgprice',
            headerName: <strong style={{ color: '#24803F' }}>Original Price</strong>,
            width: 160,
            renderCell: (params) => (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                >
                    <strong>{params.value}</strong>
                </motion.div>
            ),
        },
        {
            field: 'price',
            headerName: <strong style={{ color: '#24803F' }}>Offer Price</strong>,
            width: 160,
            renderCell: (params) => (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                >
                    <strong>{params.value}</strong>
                </motion.div>
            ),
        },
        {
            field: 'status',
            headerName: <strong style={{ color: '#24803F' }}>Status</strong>,
            width: 100,
            renderCell: (params) => (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                >
                    <Chip
                        label={params.value}
                        color={params.value === 'Approved' ? 'success' : 'default'}
                        size='small'
                    />
                </motion.div>
            ),
        },
    ];

    if (loading) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress />
                </Box>
            </motion.div>
        );
    }

    if (error) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Box sx={{ mt: 8 }}>
                    <Typography color='error'>Error: {error}</Typography>
                </Box>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <Box sx={{ mt: 2, borderRadius: 2, p: 3, bgcolor: '#fff', boxShadow: 1 }}>
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Typography variant='h4' component='h1' sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                        Product
                    </Typography>
                    <Typography variant='body2' color='#2e7d32' sx={{ mb: 2 }}>
                        Dashboard • Product • List
                    </Typography>
                </motion.div>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                                placeholder='Search...'
                                size='small'
                                InputProps={{
                                    startAdornment: <FiSearch style={{ marginRight: 8, color: '#9ca3af' }} />,
                                }}
                            />
                        </Box>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant='outlined'
                                color='primary'
                                sx={{
                                    borderColor: '#9ca3af',
                                    color: '#9ca3af',
                                    '&:hover': { borderColor: 'tomato', color: 'red' },
                                }}
                                startIcon={<DeleteIcon />}
                                onClick={handleConfirmDeleteOpen}
                                disabled={selectedRows.length === 0}
                            >
                                Delete Product ({selectedRows.length})
                            </Button>
                            <Button
                                variant='contained'
                                color='primary'
                                sx={{
                                    backgroundColor: '#2e7d32',
                                    '&:hover': { backgroundColor: '#45a049' },
                                }}
                                onClick={handleOpenCreate}
                            >
                                Add Product
                            </Button>
                        </Box>
                    </motion.div>
                </Box>
                <DataGrid
                    rows={products}
                    columns={columns}
                    rowCount={products.length}
                    paginationMode='client'
                    initialState={{
                        pagination: {
                            paginationModel: { page: page, pageSize: rowsPerPage },
                        },
                    }}
                    pageSizeOptions={[10, 25, 50]}
                    onPaginationModelChange={(model) => {
                        setPage(model.page);
                        setRowsPerPage(model.pageSize);
                    }}
                    disableRowSelectionOnClick
 autoHeight
                    rowHeight={100}
                    getRowId={(row) => row.id}
                    sx={{
                        borderRadius: 2,
                        '& .MuiDataGrid-columnHeaders': { bgcolor: '#f9fafb' },
                    }}
                />

                <ProductEdit
                    open={openEdit}
                    handleClose={handleClose}
                    product={selectedProduct}
                    onProductUpdated={handleProductUpdated}
                />

                <Modal
                    open={openCreate}
                    onClose={handleCloseCreate}
                    aria-labelledby='create-product-modal-title'
                    aria-describedby='create-product-modal-description'
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 650,
                                maxHeight: '80vh',
                                overflowY: 'auto',
                                bgcolor: 'background.paper',
                                boxShadow: 24,
                                p: 4,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <ProductCreate
                                onProductCreated={handleProductCreated}
                                handleClose={handleCloseCreate}
                            />
                        </Box>
                    </motion.div>
                </Modal>

                <Modal
                    open={confirmDelete}
                    onClose={handleConfirmDeleteClose}
                    aria-labelledby='confirm-delete-modal-title'
                    aria-describedby='confirm-delete-modal-description'
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 400,
                                bgcolor: 'background.paper',
                                boxShadow: 24,
                                p: 4,
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <Typography id='confirm-delete-modal-title' variant='h6' component='h2' sx={{ color: 'red' }}>
                                Confirm Deletion
                            </Typography>
                            <Typography id='confirm-delete-modal-description' variant='body1'>
                                Are you sure you want to delete {selectedRows.length} selected product(s)?
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Button
                                    variant='outlined'
                                    onClick={handleConfirmDeleteClose}
                                    sx={{ color: '#9ca3af', borderColor: '#9ca3af' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant='contained'
                                    color='error'
                                    onClick={handleBulkDelete}
                                    sx={{ backgroundColor: 'red', '&:hover': { backgroundColor: 'darkred' } }}
                                >
                                    Delete
                                </Button>
                            </Box>
                        </Box>
                    </motion.div>
                </Modal>

                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbarSeverity}
                        sx={{ width: '100%' }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </motion.div>
    );
};

export default ProductList;